import React, { useState, useEffect } from 'react';
import { Plus, Search, Upload, X, Trash2, ChevronDown, FileText, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSignedUrls } from '../../hooks/useSignedUrl';

interface Inspection {
  id: string;
  driver_id: string;
  driver_name: string;
  date: string;
  report_number: string;
  sms: 'YES' | 'NO';
  acc_charge_bonus: 'YES' | 'NO - otisao je' | 'No - will not be charged' | 'NO';
  training: 'No, not needed' | 'YES - finished' | 'otisao' | 'NOT FINISHED';
  citation: 'YES' | 'NO' | 'YES-WARNING';
  certificate_urls: string[];
  level: 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI';
  clean_status: 'CLEAN' | 'NOT CLEAN';
  oos: 'YES' | 'NO';
  faxed: 'YES' | 'NO' | 'YES - no needed';
  original_office: 'YES' | 'NO';
  alert: string;
  state: string;
  company?: string;
}

interface InspectionsViewProps {
  companyFilter?: string;
}

export function InspectionsView({ companyFilter }: InspectionsViewProps = {}) {
  const toast = useToast();
  const { profile } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [showDeleteOldModal, setShowDeleteOldModal] = useState(false);
  const [deletingOld, setDeletingOld] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; first_name: string; last_name: string }>>([]);
  
  const [formData, setFormData] = useState({
    driver_id: '',
    date: new Date().toISOString().split('T')[0],
    report_number: '',
    sms: 'YES' as const,
    acc_charge_bonus: 'YES' as const,
    training: 'No, not needed' as const,
    citation: 'NO' as const,
    level: 'III' as const,
    clean_status: 'CLEAN' as const,
    oos: 'NO' as const,
    faxed: 'YES' as const,
    original_office: 'NO' as const,
    alert: '',
    state: ''
  });
  
  const [certificateFiles, setCertificateFiles] = useState<File[]>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [editModal, setEditModal] = useState<{ inspection: Inspection; field: string; options?: string[] } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [uploadModal, setUploadModal] = useState<{ inspectionId: string; currentUrls: string[] } | null>(null);
  // Certificates are in a private bucket: sign the modal's links in one request.
  const { urls: certificateSignedUrls, loading: certificateUrlsLoading } = useSignedUrls(uploadModal?.currentUrls ?? []);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [actionModal, setActionModal] = useState<Inspection | null>(null);
  const [driverEditSearch, setDriverEditSearch] = useState('');
  const [selectedInspections, setSelectedInspections] = useState<Set<string>>(new Set());
  const [bulkActionModal, setBulkActionModal] = useState<'status' | 'delete' | null>(null);
  const [bulkStatusChange, setBulkStatusChange] = useState<'CLEAN' | 'NOT CLEAN'>('CLEAN');
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});

  // Load company names from database
  useEffect(() => {
    async function loadCompanyNames() {
      const { data } = await supabase
        .from('companies')
        .select('code, name')
        .eq('is_active', true);
      
      if (data) {
        const names: Record<string, string> = {};
        data.forEach(company => {
          names[company.code.toLowerCase()] = company.name;
          names[company.code] = company.name;
        });
        setCompanyNames(names);
      }
    }
    loadCompanyNames();
  }, []);

  useEffect(() => {
    loadInspections();
    loadDrivers();
    
    // Collapse all groups by default in "All Inspections" view
    if (!companyFilter) {
      // Load companies and collapse all groups
      supabase
        .from('companies')
        .select('code')
        .eq('is_active', true)
        .then(({ data }) => {
          if (data) {
            const allStatuses = ['CLEAN', 'NOT CLEAN'];
            const allGroups = new Set<string>();
            
            data.forEach(company => {
              allStatuses.forEach(status => {
                allGroups.add(`${company.code}-${status}`);
              });
            });
            
            setCollapsedGroups(allGroups);
          }
        });
    }
  }, [companyFilter]);

  async function loadInspections() {
    try {
      let query = supabase
        .from('inspections')
        .select(`
          *,
          drivers(id, first_name, last_name, companies(name, code))
        `)
        .order('date', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      let formattedInspections = (data || []).map((item: any) => {
        const companyCode = item.drivers?.companies?.code;
        return {
          id: item.id,
          driver_id: item.driver_id,
          driver_name: item.drivers ? `${item.drivers.first_name} ${item.drivers.last_name}` : 'N/A',
          date: item.date,
          report_number: item.report_number || '',
          sms: item.sms || 'YES',
          acc_charge_bonus: item.acc_charge_bonus || 'YES',
          training: item.training || 'No, not needed',
          citation: item.citation || 'NO',
          certificate_urls: item.certificate_urls || [],
          level: item.level || 'III',
          clean_status: item.clean_status || 'CLEAN',
          oos: item.oos || 'NO',
          faxed: item.faxed || 'YES',
          original_office: item.original_office || 'NO',
          alert: item.alert || '',
          state: item.state || '',
          company: companyCode || 'UNKNOWN',
        };
      });

      if (companyFilter) {
        formattedInspections = formattedInspections.filter(
          (inspection: Inspection) => inspection.company?.toLowerCase() === companyFilter.toLowerCase()
        );
      }

      setInspections(formattedInspections);
    } catch (error) {
      console.error('Error loading inspections:', error);
    }
  }

  async function loadDrivers() {
    let query = supabase
      .from('drivers')
      .select('id, first_name, last_name, companies(code)');
    
    if (companyFilter) {
      query = query.eq('companies.code', companyFilter.toUpperCase());
    }
    
    const { data } = await query.order('first_name');
    setDrivers(data || []);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload certificates if any
      const certificateUrls: string[] = [];
      
      for (const file of certificateFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `inspection-certificates/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('documents')
          .getPublicUrl(filePath);

        certificateUrls.push(publicUrl);
      }

      const { data: newInspection, error } = await supabase
        .from('inspections')
        .insert({
          driver_id: formData.driver_id,
          date: formData.date,
          report_number: formData.report_number,
          sms: formData.sms,
          acc_charge_bonus: formData.acc_charge_bonus,
          training: formData.training,
          citation: formData.citation,
          certificate_urls: certificateUrls,
          level: formData.level,
          clean_status: formData.clean_status,
          oos: formData.oos,
          faxed: formData.faxed,
          original_office: formData.original_office,
          alert: formData.alert,
          state: formData.state,
        })
        .select('id')
        .single();

      if (error) throw error;

      if (newInspection) {
        const { emailReminders } = await import('../../services/emailReminders');
        void emailReminders.inspectionCreated({
          id: newInspection.id, date: formData.date,
          report_number: formData.report_number, level: formData.level,
          clean_status: formData.clean_status,
        });
      }

      toast.success('Inspection added successfully!');
      setShowModal(false);
      setFormData({
        driver_id: '',
        date: new Date().toISOString().split('T')[0],
        report_number: '',
        sms: 'YES',
        acc_charge_bonus: 'YES',
        training: 'No, not needed',
        citation: 'NO',
        level: 'III',
        clean_status: 'CLEAN',
        oos: 'NO',
        faxed: 'YES',
        original_office: 'NO',
        alert: '',
        state: ''
      });
      setCertificateFiles([]);
      loadInspections();
    } catch (error) {
      console.error('Error adding inspection:', error);
      toast.error('Failed to add inspection');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteInspection(id: string) {
    if (!confirm('Are you sure you want to delete this inspection?')) return;

    try {
      const { error } = await supabase
        .from('inspections')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Inspection deleted successfully!');
      loadInspections();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error('Failed to delete inspection');
    }
  }

  async function handleDeleteOldInspections() {
    try {
      setDeletingOld(true);
      
      // Calculate date 2 years ago
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      const cutoffDate = twoYearsAgo.toISOString().split('T')[0];

      // Delete inspections older than 2 years
      const { data, error } = await supabase
        .from('inspections')
        .delete()
        .lt('date', cutoffDate)
        .select();

      if (error) throw error;

      const deletedCount = data?.length || 0;
      
      if (deletedCount === 0) {
        toast.info('No inspections older than 2 years found');
      } else {
        toast.success(`Successfully deleted ${deletedCount} inspection(s) older than 2 years`);
      }
      
      setShowDeleteOldModal(false);
      loadInspections();
    } catch (error) {
      console.error('Error deleting old inspections:', error);
      toast.error('Failed to delete old inspections');
    } finally {
      setDeletingOld(false);
    }
  }

  async function handleCellEdit(id: string, field: string, value: string) {
    try {
      const { error } = await supabase
        .from('inspections')
        .update({ [field]: value })
        .eq('id', id);

      if (error) throw error;

      toast.success('Updated successfully!');
      loadInspections();
      setEditModal(null);
    } catch (error) {
      console.error('Error updating inspection:', error);
      toast.error('Failed to update inspection');
    }
  }

  function openEditModal(inspection: Inspection, field: string, options?: string[]) {
    setEditModal({ inspection, field, options });
    if (field === 'driver_id') {
      setDriverEditSearch('');
      setEditValue(inspection.driver_id);
    } else if (field === 'date') {
      setEditValue(inspection.date);
    } else {
      setEditValue(inspection[field as keyof Inspection] as string || '');
    }
  }

  function closeEditModal() {
    setEditModal(null);
    setEditValue('');
    setDriverEditSearch('');
  }

  function handleSaveEdit() {
    if (editModal) {
      handleCellEdit(editModal.inspection.id, editModal.field, editValue);
    }
  }

  async function handleCertificateUpload(inspectionId: string) {
    if (uploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    try {
      setLoading(true);
      const uploadedUrls: string[] = [];

      for (const file of uploadFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${inspectionId}-${Date.now()}.${fileExt}`;
        const filePath = `inspection-certificates/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      // Get current certificate URLs and append new ones
      const currentUrls = uploadModal?.currentUrls || [];
      const allUrls = [...currentUrls, ...uploadedUrls];

      const { error: updateError } = await supabase
        .from('inspections')
        .update({ certificate_urls: allUrls })
        .eq('id', inspectionId);

      if (updateError) throw updateError;

      toast.success('Certificates uploaded successfully!');
      setUploadModal(null);
      setUploadFiles([]);
      loadInspections();
    } catch (error) {
      console.error('Error uploading certificates:', error);
      toast.error('Failed to upload certificates');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteCertificate(inspectionId: string, urlToDelete: string) {
    if (!confirm('Are you sure you want to delete this certificate?')) return;

    try {
      setLoading(true);
      
      // Get current certificate URLs and remove the one to delete
      const currentUrls = uploadModal?.currentUrls || [];
      const updatedUrls = currentUrls.filter(url => url !== urlToDelete);

      const { error } = await supabase
        .from('inspections')
        .update({ certificate_urls: updatedUrls })
        .eq('id', inspectionId);

      if (error) throw error;

      toast.success('Certificate deleted successfully!');
      
      // Update modal state
      if (uploadModal) {
        setUploadModal({ ...uploadModal, currentUrls: updatedUrls });
      }
      
      loadInspections();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Failed to delete certificate');
    } finally {
      setLoading(false);
    }
  }

  function openUploadModal(inspectionId: string, currentUrls: string[]) {
    setUploadModal({ inspectionId, currentUrls });
    setUploadFiles([]);
  }

  function closeUploadModal() {
    setUploadModal(null);
    setUploadFiles([]);
  }

  function openActionModal(inspection: Inspection) {
    setActionModal(inspection);
  }

  function closeActionModal() {
    setActionModal(null);
  }

  async function handleDriverChange(inspectionId: string, newDriverId: string) {
    try {
      const { error } = await supabase
        .from('inspections')
        .update({ driver_id: newDriverId })
        .eq('id', inspectionId);

      if (error) throw error;

      toast.success('Driver updated successfully!');
      loadInspections();
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error('Failed to update driver');
    }
  }

  function toggleGroupCollapse(status: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }

  function toggleSelectInspection(inspectionId: string) {
    setSelectedInspections(prev => {
      const next = new Set(prev);
      if (next.has(inspectionId)) {
        next.delete(inspectionId);
      } else {
        next.add(inspectionId);
      }
      return next;
    });
  }

  function toggleSelectAll(inspectionIds: string[]) {
    const allSelected = inspectionIds.every(id => selectedInspections.has(id));
    if (allSelected) {
      setSelectedInspections(prev => {
        const next = new Set(prev);
        inspectionIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedInspections(prev => {
        const next = new Set(prev);
        inspectionIds.forEach(id => next.add(id));
        return next;
      });
    }
  }

  async function handleBulkStatusChange() {
    try {
      setLoading(true);
      const selectedIds = Array.from(selectedInspections);
      
      const { error } = await supabase
        .from('inspections')
        .update({ clean_status: bulkStatusChange })
        .in('id', selectedIds);

      if (error) throw error;

      toast.success(`Updated ${selectedIds.length} inspection(s) to ${bulkStatusChange}`);
      setBulkActionModal(null);
      setSelectedInspections(new Set());
      loadInspections();
    } catch (error) {
      console.error('Error updating inspections:', error);
      toast.error('Failed to update inspections');
    } finally {
      setLoading(false);
    }
  }

  async function handleBulkDelete() {
    try {
      setLoading(true);
      const selectedIds = Array.from(selectedInspections);
      
      const { error } = await supabase
        .from('inspections')
        .delete()
        .in('id', selectedIds);

      if (error) throw error;

      toast.success(`Deleted ${selectedIds.length} inspection(s)`);
      setBulkActionModal(null);
      setSelectedInspections(new Set());
      loadInspections();
    } catch (error) {
      console.error('Error deleting inspections:', error);
      toast.error('Failed to delete inspections');
    } finally {
      setLoading(false);
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const searchLower = searchTerm.toLowerCase();
    return !searchTerm || 
      inspection.driver_name.toLowerCase().includes(searchLower) ||
      inspection.report_number.toLowerCase().includes(searchLower) ||
      inspection.state.toLowerCase().includes(searchLower);
  });

  // Group inspections by company and status
  const groupedInspections = companyFilter
    ? // If company filter is set, group by status only
      filteredInspections.reduce((acc, inspection) => {
        const status = inspection.clean_status;
        if (!acc[status]) {
          acc[status] = [];
        }
        acc[status].push(inspection);
        return acc;
      }, {} as Record<string, typeof inspections>)
    : // If no company filter, group by company then status
      filteredInspections.reduce((acc, inspection) => {
        const company = (inspection.company || 'UNKNOWN').toUpperCase();
        const status = inspection.clean_status;
        const key = `${company}-${status}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(inspection);
        return acc;
      }, {} as Record<string, typeof inspections>);

  const getStatusLabel = (key: string) => {
    if (companyFilter) {
      // Company-specific view
      const status = key;
      const companyPrefix = companyNames[companyFilter] || companyNames[companyFilter.toLowerCase()] || 'Company';
      if (status === 'CLEAN') {
        return `${companyPrefix} Clean Inspections`;
      } else {
        return `${companyPrefix} Not Clean Inspections`;
      }
    } else {
      // All inspections view - show company + status
      const [company, status] = key.split('-');
      // Get company name from companyNames state
      const companyName = company === 'UNKNOWN' ? 'Unknown Company' : 
                         companyNames[company] || companyNames[company.toLowerCase()] || company;
      if (status === 'CLEAN') {
        return `${companyName} Clean Inspections`;
      } else {
        return `${companyName} Not Clean Inspections`;
      }
    }
  };

  const getStatusColor = (key: string) => {
    const status = companyFilter ? key : key.split('-')[1];
    if (status === 'CLEAN') {
      return {
        color: 'from-green-50 to-green-100 border-green-500',
        textColor: 'text-green-700'
      };
    } else {
      return {
        color: 'from-red-50 to-red-100 border-red-500',
        textColor: 'text-red-700'
      };
    }
  };

  // Sort groups: if company filter, sort by status; otherwise by company then status
  const sortedGroups = companyFilter
    ? ['CLEAN', 'NOT CLEAN']
    : (() => {
        // Get all company codes from the companyNames state
        const allCompanyCodes = Object.keys(companyNames).filter(key => key === key.toUpperCase());
        const allStatuses = ['CLEAN', 'NOT CLEAN'];
        const allGroups: string[] = [];
        
        // Create all possible company-status combinations
        allCompanyCodes.forEach(company => {
          allStatuses.forEach(status => {
            allGroups.push(`${company}-${status}`);
          });
        });
        
        // Filter out UNKNOWN company groups - don't show them in the UI
        // Object.keys(groupedInspections).forEach(key => {
        //   if (!allGroups.includes(key) && !key.startsWith('UNKNOWN-')) {
        //     allGroups.push(key);
        //   }
        // });
        
        return allGroups;
      })();


  const filteredDrivers = drivers.filter(driver => {
    const searchLower = driverSearch.toLowerCase();
    return !driverSearch || 
      `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(searchLower);
  });

  return (
    <div className="space-y-6">
      {companyFilter && (
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">{companyNames[companyFilter] || companyNames[companyFilter.toLowerCase()] || 'Company'} Inspections</h1>
          <p className="text-slate-600 mt-1">Manage inspections for {companyNames[companyFilter] || companyNames[companyFilter.toLowerCase()] || 'this company'}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search inspections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Remove Old Inspections button - only visible to Safety Manager, Admin, and Superadmin */}
          {(profile?.role === 'Safety Manager' || profile?.role === 'Admin' || profile?.role === 'Superadmin') && (
            <button 
              onClick={() => setShowDeleteOldModal(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              <Trash2 className="w-5 h-5" />
              Remove Old Inspections
            </button>
          )}
          
          <button 
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            <Plus className="w-5 h-5" />
            Add Inspection
          </button>
        </div>
      </div>

      {/* Inspections Table - Monday.com Style */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody>
              {sortedGroups.map((groupKey) => {
                const statusInspections = groupedInspections[groupKey] || [];
                const statusGroup = getStatusColor(groupKey);
                const isCollapsed = collapsedGroups.has(groupKey);
                
                return (
                  <React.Fragment key={groupKey}>
                    {/* Status Header Row */}
                    <tr>
                      <td colSpan={15} className="p-0">
                        <div 
                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 bg-gradient-to-r ${statusGroup.color} border-l-4`}
                          onClick={() => toggleGroupCollapse(groupKey)}
                        >
                          <div>
                            <h3 className="font-bold text-base text-slate-900">{getStatusLabel(groupKey)}</h3>
                            <p className="text-xs text-slate-600">
                              {statusInspections.length} {statusInspections.length === 1 ? 'inspection' : 'inspections'}
                            </p>
                          </div>
                          <div className={`transform transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}>
                            <ChevronDown className="w-5 h-5 text-slate-600" />
                          </div>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Column Headers */}
                    {!isCollapsed && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-2 py-2 text-center w-12">
                          <input
                            type="checkbox"
                            checked={statusInspections.length > 0 && statusInspections.every(i => selectedInspections.has(i.id))}
                            onChange={() => toggleSelectAll(statusInspections.map(i => i.id))}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Driver</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">SMS</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ACC</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Training</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Date</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Report #</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Citation</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Certificate</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Level</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">OOS</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Faxed</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Original</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">State</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Alert</th>
                      </tr>
                    )}
                    
                    {/* Inspection Rows */}
                    {!isCollapsed && statusInspections.map((inspection) => (
                      <tr key={inspection.id} className="border-b border-slate-200 hover:bg-slate-50/50 transition-all">
                        <td className="px-2 py-2 text-center w-12">
                          <input
                            type="checkbox"
                            checked={selectedInspections.has(inspection.id)}
                            onChange={() => toggleSelectInspection(inspection.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2 min-w-[180px] cursor-pointer hover:bg-slate-100 transition-colors relative group" onClick={() => openEditModal(inspection, 'driver_id')}>
                          <span className="text-xs font-semibold text-slate-900 uppercase tracking-wide">{inspection.driver_name}</span>
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[60px] border-r-2 border-white ${inspection.sms === 'YES' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`} onClick={() => openEditModal(inspection, 'sms', ['YES', 'NO'])}>
                          {inspection.sms}
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[180px] border-r-2 border-white ${inspection.acc_charge_bonus === 'YES' ? 'bg-emerald-500 text-white' : 'bg-cyan-400 text-white'}`} onClick={() => openEditModal(inspection, 'acc_charge_bonus', ['YES', 'NO - otisao je', 'No - will not be charged', 'NO'])}>
                          {inspection.acc_charge_bonus}
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[140px] border-r-2 border-white ${inspection.training === 'YES - finished' ? 'bg-emerald-500 text-white' : inspection.training === 'NOT FINISHED' ? 'bg-red-500 text-white' : inspection.training === 'otisao' ? 'bg-lime-400 text-white' : 'bg-cyan-400 text-white'}`} onClick={() => openEditModal(inspection, 'training', ['No, not needed', 'YES - finished', 'otisao', 'NOT FINISHED'])}>
                          {inspection.training}
                        </td>
                        <td className="px-3 py-2 min-w-[100px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openEditModal(inspection, 'date')}>
                          <span className="text-xs text-slate-700 font-medium">
                            {(() => {
                              const dateStr = inspection.date.split('T')[0];
                              const [y, m, d] = dateStr.split('-');
                              return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString();
                            })()}
                          </span>
                        </td>
                        <td className="px-3 py-2 min-w-[120px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openEditModal(inspection, 'report_number')}>
                          <span className="text-xs font-bold text-blue-600">{inspection.report_number}</span>
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[60px] border-r-2 border-white ${inspection.citation === 'YES' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`} onClick={() => openEditModal(inspection, 'citation', ['YES', 'NO', 'YES-WARNING'])}>
                          {inspection.citation}
                        </td>
                        <td className="px-3 py-2 min-w-[80px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openUploadModal(inspection.id, inspection.certificate_urls)}>
                          <div className="flex items-center justify-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            {inspection.certificate_urls.length > 0 && (
                              <div className="relative">
                                <div className="absolute -top-1 -right-1 bg-slate-700 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                  +{inspection.certificate_urls.length}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[50px] border-r-2 border-white ${inspection.level === 'I' ? 'bg-purple-500 text-white' : inspection.level === 'II' ? 'bg-indigo-500 text-white' : 'bg-blue-500 text-white'}`} onClick={() => openEditModal(inspection, 'level', ['I', 'II', 'III', 'IV', 'V', 'VI'])}>
                          {inspection.level}
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[60px] border-r-2 border-white ${inspection.oos === 'YES' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`} onClick={() => openEditModal(inspection, 'oos', ['YES', 'NO'])}>
                          {inspection.oos}
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[120px] border-r-2 border-white ${inspection.faxed === 'YES' ? 'bg-emerald-500 text-white' : inspection.faxed === 'YES - no needed' ? 'bg-blue-500 text-white' : 'bg-slate-400 text-white'}`} onClick={() => openEditModal(inspection, 'faxed', ['YES', 'NO', 'YES - no needed'])}>
                          {inspection.faxed}
                        </td>
                        <td className={`px-3 py-2 cursor-pointer text-xs font-bold text-center min-w-[60px] border-r-2 border-white ${inspection.original_office === 'YES' ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}`} onClick={() => openEditModal(inspection, 'original_office', ['YES', 'NO'])}>
                          {inspection.original_office}
                        </td>
                        <td className="px-3 py-2 cursor-pointer text-xs font-bold text-center bg-indigo-500 text-white min-w-[50px] border-r-2 border-white" onClick={() => openEditModal(inspection, 'state')}>
                          {inspection.state}
                        </td>
                        <td className="px-3 py-2 min-w-[100px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => openEditModal(inspection, 'alert')}>
                          <span className={`text-xs ${inspection.alert ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{inspection.alert || '-'}</span>
                        </td>
                        <td className="px-3 py-2 text-center min-w-[60px]">
                          <button 
                            onClick={(e) => { e.stopPropagation(); openActionModal(inspection); }}
                            className="inline-flex items-center justify-center p-1.5 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Inspection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Add Inspection</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Driver */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Driver <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={driverSearch}
                      onChange={(e) => {
                        setDriverSearch(e.target.value);
                        setShowDriverDropdown(true);
                      }}
                      onFocus={() => setShowDriverDropdown(true)}
                      placeholder="Search driver..."
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {showDriverDropdown && driverSearch && filteredDrivers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredDrivers.map((driver) => (
                          <button
                            key={driver.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, driver_id: driver.id });
                              setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                              setShowDriverDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors"
                          >
                            {driver.first_name} {driver.last_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Report Number */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Report Number <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.report_number}
                    onChange={(e) => setFormData({ ...formData, report_number: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* State */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    State <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                    maxLength={2}
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                  />
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="I">I</option>
                    <option value="II">II</option>
                    <option value="III">III</option>
                    <option value="IV">IV</option>
                    <option value="V">V</option>
                    <option value="VI">VI</option>
                  </select>
                </div>

                {/* Clean Status */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Clean Status</label>
                  <select
                    value={formData.clean_status}
                    onChange={(e) => setFormData({ ...formData, clean_status: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="CLEAN">CLEAN</option>
                    <option value="NOT CLEAN">NOT CLEAN</option>
                  </select>
                </div>

                {/* SMS */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">SMS</label>
                  <select
                    value={formData.sms}
                    onChange={(e) => setFormData({ ...formData, sms: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                  </select>
                </div>

                {/* ACC Charge/Bonus */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">ACC Charge/Bonus</label>
                  <select
                    value={formData.acc_charge_bonus}
                    onChange={(e) => setFormData({ ...formData, acc_charge_bonus: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="YES">YES</option>
                    <option value="NO - otisao je">NO - otisao je</option>
                    <option value="No - will not be charged">No - will not be charged</option>
                    <option value="NO">NO</option>
                  </select>
                </div>

                {/* Training */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Training</label>
                  <select
                    value={formData.training}
                    onChange={(e) => setFormData({ ...formData, training: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="No, not needed">No, not needed</option>
                    <option value="YES - finished">YES - finished</option>
                    <option value="otisao">otisao</option>
                    <option value="NOT FINISHED">NOT FINISHED</option>
                  </select>
                </div>

                {/* Citation */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Citation</label>
                  <select
                    value={formData.citation}
                    onChange={(e) => setFormData({ ...formData, citation: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="NO">NO</option>
                    <option value="YES">YES</option>
                    <option value="YES-WARNING">YES-WARNING</option>
                  </select>
                </div>

                {/* OOS */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">OOS (Out of Service)</label>
                  <select
                    value={formData.oos}
                    onChange={(e) => setFormData({ ...formData, oos: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="NO">NO</option>
                    <option value="YES">YES</option>
                  </select>
                </div>

                {/* Faxed */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Faxed</label>
                  <select
                    value={formData.faxed}
                    onChange={(e) => setFormData({ ...formData, faxed: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                    <option value="YES - no needed">YES - no needed</option>
                  </select>
                </div>

                {/* Original Office */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">ORIGINAL - Office</label>
                  <select
                    value={formData.original_office}
                    onChange={(e) => setFormData({ ...formData, original_office: e.target.value as any })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="NO">NO</option>
                    <option value="YES">YES</option>
                  </select>
                </div>
              </div>

              {/* Alert */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Alert / Notes</label>
                <textarea
                  value={formData.alert}
                  onChange={(e) => setFormData({ ...formData, alert: e.target.value })}
                  rows={3}
                  placeholder="e.g., unsafe, maintenance OOS, overweight, etc."
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Certificate Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Certificate Files</label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files) {
                        setCertificateFiles(Array.from(e.target.files));
                      }
                    }}
                    className="hidden"
                    id="certificate-upload"
                  />
                  <label
                    htmlFor="certificate-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-600">Click to upload certificates</span>
                    <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 10MB</span>
                  </label>
                  {certificateFiles.length > 0 && (
                    <div className="mt-4 text-left">
                      <p className="text-sm font-medium text-slate-700 mb-2">Selected files:</p>
                      {certificateFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded mb-1">
                          <span className="text-sm text-slate-700">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setCertificateFiles(files => files.filter((_, i) => i !== idx))}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
                >
                  {loading ? 'Adding...' : 'Add Inspection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full">
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Row Actions</h3>
              
              <button
                onClick={() => {
                  closeActionModal();
                  handleDeleteInspection(actionModal.id);
                }}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Inspection
              </button>

              <button
                onClick={closeActionModal}
                className="w-full px-4 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Field Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Edit {editModal.field.replace('_', ' ').toUpperCase()}</h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  {editModal.field === 'driver_id' ? 'Select Driver' : 'Select Value'}
                </label>
                
                {editModal.field === 'driver_id' ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={driverEditSearch}
                      onChange={(e) => setDriverEditSearch(e.target.value)}
                      placeholder="Search driver..."
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                      autoFocus
                    />
                    {driverEditSearch && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {drivers.filter(driver => 
                          `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverEditSearch.toLowerCase())
                        ).map((driver) => (
                          <button
                            key={driver.id}
                            type="button"
                            onClick={() => {
                              setEditValue(driver.id);
                              setDriverEditSearch(`${driver.first_name} ${driver.last_name}`);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-0"
                          >
                            {driver.first_name} {driver.last_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : editModal.field === 'date' ? (
                  <input
                    type="date"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    autoFocus
                  />
                ) : editModal.options ? (
                  <div className="space-y-2">
                    {editModal.options.map((option) => (
                      <button
                        key={option}
                        onClick={() => setEditValue(option)}
                        className={`w-full px-4 py-3 rounded-lg border-2 transition-all text-left font-medium ${
                          editValue === option
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : editModal.field === 'alert' ? (
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    placeholder="Enter alert/notes..."
                    autoFocus
                  />
                ) : (
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    placeholder="Enter value..."
                    autoFocus
                  />
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeEditModal}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Upload Modal */}
      {uploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Upload Certificates</h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Current Certificates */}
              {uploadModal.currentUrls.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Current Certificates ({uploadModal.currentUrls.length})
                  </label>
                  <div className="space-y-2">
                    {uploadModal.currentUrls.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <FileText className="w-4 h-4 text-blue-600" />
                        {certificateUrlsLoading ? (
                          <span className="text-sm text-blue-600 opacity-60 cursor-wait flex-1 truncate" aria-busy="true">
                            Certificate {idx + 1}
                          </span>
                        ) : certificateSignedUrls.get(url.trim()) ? (
                          <a
                            href={certificateSignedUrls.get(url.trim())}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline flex-1 truncate"
                          >
                            Certificate {idx + 1}
                          </a>
                        ) : (
                          <span className="text-sm text-slate-400 flex-1 truncate" title="This file could not be opened">
                            Certificate {idx + 1} (unavailable)
                          </span>
                        )}
                        <button
                          onClick={() => handleDeleteCertificate(uploadModal.inspectionId, url)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete certificate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Add New Certificates
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files) {
                        setUploadFiles(Array.from(e.target.files));
                      }
                    }}
                    className="hidden"
                    id="certificate-upload-modal"
                  />
                  <label
                    htmlFor="certificate-upload-modal"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-600">Click to upload certificates</span>
                    <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 10MB</span>
                  </label>
                  {uploadFiles.length > 0 && (
                    <div className="mt-4 text-left">
                      <p className="text-sm font-medium text-slate-700 mb-2">Selected files:</p>
                      {uploadFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded mb-1 border border-slate-200">
                          <span className="text-sm text-slate-700">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setUploadFiles(files => files.filter((_, i) => i !== idx))}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeUploadModal}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleCertificateUpload(uploadModal.inspectionId)}
                  disabled={loading || uploadFiles.length === 0}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Change Modal */}
      {bulkActionModal === 'status' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Change Status</h3>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-slate-600">
                Change status for <span className="font-bold text-slate-900">{selectedInspections.size}</span> selected inspection(s)
              </p>

              <div className="space-y-2">
                <button
                  onClick={() => setBulkStatusChange('CLEAN')}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-all text-left font-medium ${
                    bulkStatusChange === 'CLEAN'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-green-300 hover:bg-slate-50'
                  }`}
                >
                  CLEAN
                </button>
                <button
                  onClick={() => setBulkStatusChange('NOT CLEAN')}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-all text-left font-medium ${
                    bulkStatusChange === 'NOT CLEAN'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-red-300 hover:bg-slate-50'
                  }`}
                >
                  NOT CLEAN
                </button>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setBulkActionModal(null)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkStatusChange}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {bulkActionModal === 'delete' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Delete Inspections</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-semibold mb-1">Are you sure?</p>
                  <p className="text-slate-600 text-sm">
                    You are about to delete <span className="font-bold text-slate-900">{selectedInspections.size}</span> inspection(s). This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setBulkActionModal(null)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? 'Deleting...' : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Old Inspections Confirmation Modal */}
      {showDeleteOldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Delete Old Inspections</h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-semibold mb-1">Are you sure?</p>
                  <p className="text-slate-600 text-sm">
                    This will permanently delete all inspections older than <span className="font-bold text-slate-900">2 years</span>. This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowDeleteOldModal(false)}
                  disabled={deletingOld}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOldInspections}
                  disabled={deletingOld}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingOld ? (
                    'Deleting...'
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete Old Inspections
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Bar */}
      {selectedInspections.size > 0 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 px-6 py-4 flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                {selectedInspections.size}
              </div>
              <span className="font-semibold text-slate-900">
                {selectedInspections.size === 1 ? 'Inspection' : 'Inspections'} selected
              </span>
            </div>

            <div className="h-8 w-px bg-slate-300"></div>

            <button
              onClick={() => setBulkActionModal('status')}
              className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"></path>
              </svg>
              <span className="font-medium">Change Status</span>
            </button>

            <button
              onClick={() => setBulkActionModal('delete')}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span className="font-medium">Delete</span>
            </button>

            <button
              onClick={() => setSelectedInspections(new Set())}
              className="ml-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
